import { useState, useEffect, useCallback } from 'react';
import { servicesMonitorApi } from '../../api/servicesMonitor.api';
import { useAuth } from '../../store/AuthContext';
import { useUI } from '../../store/UIContext';
import { PageHeader, Card, Badge, Btn, Spinner } from '../../components/ui/index';

const CARD_BOX = { display: 'flex', flexDirection: 'column', gap: 10 };
const TEXTAREA = {
  width: '100%', boxSizing: 'border-box', padding: '10px 14px', border: '1.5px solid #e2e8f0',
  borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', minHeight: 64,
};
const RESULT_BOX = {
  background: '#f8faff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px',
  fontSize: 12, color: '#334155', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.8,
};

function StatusBadge({ info }) {
  if (!info) return <Badge variant="gray">نامشخص</Badge>;
  const active = info.active === 'active';
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <Badge variant={active ? 'green' : 'red'}>{active ? '🟢 در حال اجرا' : `🔴 ${info.active || 'متوقف'}`}</Badge>
      {info.enabled && <Badge variant="blue">{info.enabled === 'enabled' ? 'فعال در بوت' : info.enabled}</Badge>}
    </div>
  );
}

function ServiceCard({ title, desc, info, loadingStatus, canRestart, onRestart, restarting, children }) {
  return (
    <Card style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>{title}</h3>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>{desc}</p>
        </div>
        {canRestart && (
          <Btn size="sm" variant="secondary" onClick={onRestart} loading={restarting}>
            ریستارت سرویس
          </Btn>
        )}
      </div>

      {loadingStatus ? <Spinner size={16} /> : <StatusBadge info={info} />}
      {info?.since && <div style={{ fontSize: 11, color: '#94a3b8' }}>از: {info.since}</div>}
      {info?.main_pid && <div style={{ fontSize: 11, color: '#94a3b8' }}>PID: {info.main_pid}</div>}

      <div style={CARD_BOX}>{children}</div>
    </Card>
  );
}

export default function AIServicesMonitorPage() {
  const { user } = useAuth();
  const { addToast } = useUI();
  const isAdmin = user?.type === 'admin';

  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [restarting, setRestarting] = useState(null);

  const [summarizerInput, setSummarizerInput] = useState('');
  const [summarizerResult, setSummarizerResult] = useState(null);
  const [summarizerLoading, setSummarizerLoading] = useState(false);

  const [llmInput, setLlmInput] = useState('');
  const [llmResult, setLlmResult] = useState(null);
  const [llmLoading, setLlmLoading] = useState(false);

  const [ticketInput, setTicketInput] = useState('');
  const [ticketResult, setTicketResult] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(false);

  const [kbInput, setKbInput] = useState('');
  const [kbResult, setKbResult] = useState(null);
  const [kbLoading, setKbLoading] = useState(false);

  const loadStatus = useCallback(() => {
    setLoadingStatus(true);
    servicesMonitorApi.getStatus()
      .then((res) => setStatus(res.data || {}))
      .catch(() => addToast('خطا در دریافت وضعیت سرویس‌ها', 'error'))
      .finally(() => setLoadingStatus(false));
  }, [addToast]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const restart = async (name) => {
    setRestarting(name);
    try {
      await servicesMonitorApi.restart(name);
      addToast('سرویس ریستارت شد', 'success');
      setTimeout(loadStatus, 1500);
    } catch (err) {
      addToast(err.response?.data?.error || 'ریستارت سرویس ناموفق بود', 'error');
    }
    setRestarting(null);
  };

  const runTest = async (kind) => {
    if (kind === 'summarizer') {
      if (!summarizerInput.trim()) return;
      setSummarizerLoading(true);
      try {
        const res = await servicesMonitorApi.testSummarizer(summarizerInput.trim());
        setSummarizerResult(res.data);
      } catch (err) {
        addToast(err.response?.data?.error || 'تست سرویس ناموفق بود', 'error');
        setSummarizerResult(null);
      }
      setSummarizerLoading(false);
    } else if (kind === 'llm') {
      if (!llmInput.trim()) return;
      setLlmLoading(true);
      try {
        const res = await servicesMonitorApi.testLLM(llmInput.trim());
        setLlmResult(res.data);
      } catch (err) {
        addToast(err.response?.data?.error || 'تست سرویس ناموفق بود', 'error');
        setLlmResult(null);
      }
      setLlmLoading(false);
    } else if (kind === 'ticket') {
      if (!ticketInput.trim()) return;
      setTicketLoading(true);
      try {
        const res = await servicesMonitorApi.testTicketPipeline(ticketInput.trim());
        setTicketResult(res.data);
      } catch (err) {
        addToast(err.response?.data?.error || 'تست سرویس ناموفق بود', 'error');
        setTicketResult(null);
      }
      setTicketLoading(false);
    } else if (kind === 'kb') {
      if (!kbInput.trim()) return;
      setKbLoading(true);
      try {
        const res = await servicesMonitorApi.testLLMWithKnowledgeBase(kbInput.trim());
        setKbResult(res.data);
      } catch (err) {
        addToast(err.response?.data?.error || 'تست سرویس ناموفق بود', 'error');
        setKbResult(null);
      }
      setKbLoading(false);
    }
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="وضعیت سرویس‌های هوش مصنوعی"
        subtitle="مشاهده وضعیت، تست عملکرد واقعی، و (برای ادمین) ریستارت هر سرویس به‌صورت مجزا"
        action={<Btn size="sm" variant="secondary" onClick={loadStatus}>بروزرسانی وضعیت</Btn>}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 720 }}>
        <ServiceCard
          title="سرویس خلاصه‌ساز (Summarizer)"
          desc="مدل mT5 که عنوان/خلاصه کوتاه از متن تیکت می‌سازد"
          info={status?.summarizer}
          loadingStatus={loadingStatus}
          canRestart={isAdmin}
          restarting={restarting === 'summarizer'}
          onRestart={() => restart('summarizer')}
        >
          <textarea style={TEXTAREA} placeholder="متنی برای خلاصه‌سازی وارد کنید..." value={summarizerInput} onChange={(e) => setSummarizerInput(e.target.value)} rows={3} />
          <Btn size="sm" onClick={() => runTest('summarizer')} loading={summarizerLoading} disabled={!summarizerInput.trim()}>تست سرویس</Btn>
          {summarizerResult && (
            <div style={RESULT_BOX}>
              <b>عنوان:</b> {summarizerResult.title || '—'}
              {summarizerResult.latency_ms != null && <div style={{ color: '#94a3b8', marginTop: 4 }}>زمان پاسخ: {Math.round(summarizerResult.latency_ms)}ms</div>}
            </div>
          )}
        </ServiceCard>

        <ServiceCard
          title="سرویس مدل زبانی (LLM)"
          desc="مدل Gemma4 خام، بدون دانش سازمانی یا کانتکست تیکتینگ"
          info={status?.gemma4}
          loadingStatus={loadingStatus}
          canRestart={isAdmin}
          restarting={restarting === 'gemma4'}
          onRestart={() => restart('gemma4')}
        >
          <textarea style={TEXTAREA} placeholder="یک سوال یا جمله برای تست بنویسید..." value={llmInput} onChange={(e) => setLlmInput(e.target.value)} rows={3} />
          <Btn size="sm" onClick={() => runTest('llm')} loading={llmLoading} disabled={!llmInput.trim()}>تست سرویس</Btn>
          {llmResult && (
            <div style={RESULT_BOX}>
              <b>پاسخ:</b> {llmResult.reply || '—'}
              {llmResult.usage?.total_tokens != null && <div style={{ color: '#94a3b8', marginTop: 4 }}>توکن مصرفی: {llmResult.usage.total_tokens}</div>}
            </div>
          )}
        </ServiceCard>

        <ServiceCard
          title="سرویس هوش مصنوعی کلی (Ticketing AI)"
          desc="مسیریابی تیکت به دپارتمان و پاسخ‌گویی مبتنی بر دانش سازمانی"
          info={status?.ticketing}
          loadingStatus={loadingStatus}
          canRestart={isAdmin}
          restarting={restarting === 'ticketing'}
          onRestart={() => restart('ticketing')}
        >
          <div>
            <p style={{ fontSize: 12, color: '#475569', margin: '0 0 6px', fontWeight: 600 }}>تست مسیریابی تیکت</p>
            <textarea style={TEXTAREA} placeholder="متن یک تیکت نمونه بنویسید..." value={ticketInput} onChange={(e) => setTicketInput(e.target.value)} rows={3} />
            <div style={{ marginTop: 8 }}>
              <Btn size="sm" onClick={() => runTest('ticket')} loading={ticketLoading} disabled={!ticketInput.trim()}>تست مسیریابی</Btn>
            </div>
            {ticketResult && (
              <div style={{ ...RESULT_BOX, marginTop: 8 }}>
                <b>خلاصه:</b> {ticketResult.summary || '—'}<br />
                <b>ارجاع شد:</b> {ticketResult.routed ? `بله → ${ticketResult.department_name || ''}` : 'خیر (زیر آستانه)'}<br />
                {ticketResult.score != null && <>
                  <b>امتیاز:</b> {ticketResult.score}<br />
                </>}
                {ticketResult.reason && <><b>علت:</b> {ticketResult.reason}</>}
              </div>
            )}
          </div>

          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
            <p style={{ fontSize: 12, color: '#475569', margin: '0 0 6px', fontWeight: 600 }}>تست پاسخ‌گویی با دانش سازمانی (RAG)</p>
            <textarea style={TEXTAREA} placeholder="یک سوال مشتری‌محور بنویسید..." value={kbInput} onChange={(e) => setKbInput(e.target.value)} rows={2} />
            <div style={{ marginTop: 8 }}>
              <Btn size="sm" onClick={() => runTest('kb')} loading={kbLoading} disabled={!kbInput.trim()}>تست پاسخ‌گویی</Btn>
            </div>
            {kbResult && (
              <div style={{ ...RESULT_BOX, marginTop: 8 }}>
                <b>پاسخ:</b> {kbResult.reply || '—'}
              </div>
            )}
          </div>
        </ServiceCard>
      </div>
    </div>
  );
}
